/* JOURNAL — what the user tells the coach, and everything they have told it.

   Two halves: a composer window, and the flat feed below it. The feed comes back
   from `GET /journal` already newest-first and already un-deduped (the table is
   append-only and has no upsert key), so this screen neither sorts nor collapses
   it — what is shown is the row order the coach reads over MCP.

   The same append-only-ness is why the composer guards double submits with a ref
   rather than with `saving` state: `POST /journal` stores the same content twice
   if it is sent twice, and unlike `/log` and `/nutrition` there is no upsert to
   absorb the mistake. Every other write in this app is idempotent; this one is
   not, and a held Enter key or a double-click must not be able to produce two
   rows. See the same argument, traced against a real backend, in Log.tsx.

   Following Log's rule: a failed read never disables the composer, and a failed
   write never clears the textarea. Typing is not blocked on network state. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Window } from '../ds'
import {
  errorMessage,
  getJournal,
  postJournal,
  type JournalEntry,
  type JournalType,
  type Sentiment,
} from '../api'
import { monoDate } from './format'
import '../styles/journal.css'

/* ── Vocabulary ──────────────────────────────────────────────────────────── */

/**
 * The three types this screen can write.
 *
 * `checkin` is deliberately absent: it renders in the feed (the tag map below
 * covers it) but there is no chip for it, because a check-in is written by the
 * coach or the backend, not by the person being coached. A composer for it would
 * be a UI for forging the other side of the conversation.
 */
type ComposableType = Exclude<JournalType, 'checkin'>

const TYPE_CHIPS: readonly { type: ComposableType; label: string }[] = [
  { type: 'meal_feedback', label: 'MEAL FEEDBACK' },
  { type: 'workout_feedback', label: 'WORKOUT FEEDBACK' },
  { type: 'note', label: 'FREE NOTE' },
]

/** Which sentiments each type offers. An empty list means the row is hidden. */
const SENTIMENT_SETS: Record<ComposableType, readonly Sentiment[]> = {
  meal_feedback: ['liked', 'disliked'],
  workout_feedback: ['too_easy', 'just_right', 'too_hard'],
  note: [],
}

/**
 * What picking a type resets the sentiment to.
 *
 * NOT the first entry of the set: meal defaults to LIKED (first) but workout
 * defaults to JUST RIGHT (second, the middle of a three-point scale). Deriving
 * it from position would silently make every workout "too easy" by default,
 * which is a different claim from "no answer yet" and one the coach would read.
 */
export const DEFAULT_SENTIMENT: Record<ComposableType, Sentiment | null> = {
  meal_feedback: 'liked',
  workout_feedback: 'just_right',
  note: null,
}

export const SENTIMENT_LABEL: Record<Sentiment, string> = {
  liked: 'LIKED',
  disliked: "DIDN'T LIKE",
  too_easy: 'TOO EASY',
  just_right: 'JUST RIGHT',
  too_hard: 'TOO HARD',
}

/** Feed tag per type. The colour is a class, so the palette stays in the CSS. */
export const TYPE_TAG: Record<JournalType, { label: string; modifier: string }> = {
  meal_feedback: { label: 'MEAL', modifier: 'meal' },
  workout_feedback: { label: 'WORKOUT', modifier: 'workout' },
  note: { label: 'NOTE', modifier: 'note' },
  checkin: { label: 'CHECK-IN', modifier: 'checkin' },
}

/** The feed's window. The server's own default, stated rather than assumed. */
const FEED_DAYS = 90

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Journal() {
  const [feed, setFeed] = useState<Load<JournalEntry[]>>({ status: 'loading' })

  // Composer. Cleared only by a confirmed write.
  const [type, setType] = useState<ComposableType>('meal_feedback')
  const [sentiment, setSentiment] = useState<Sentiment | null>(
    DEFAULT_SENTIMENT.meal_feedback,
  )
  const [text, setText] = useState('')

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const inFlight = useRef(false)

  /* The GET in flight, if any. Two saves in quick succession each ask for a
     fresh feed, and two GETs are then racing: whichever resolves LAST wins the
     `setFeed`, which is not necessarily the one that was asked for last. The
     loser can be a snapshot taken before the newest entry existed, so the entry
     the user just wrote disappears from the feed until something reloads it.

     So a new read aborts the old one rather than leaving it running. The ref
     doubles as the unmount handle — one controller, owned in one place, instead
     of one owned by the effect and another implicit in every post-save read. */
  const loading = useRef<AbortController | null>(null)

  const load = useCallback(async () => {
    loading.current?.abort()
    const controller = new AbortController()
    loading.current = controller

    setFeed({ status: 'loading' })
    try {
      const res = await getJournal({ days: FEED_DAYS }, controller.signal)
      // Belt and braces: `getJournal` rejects on abort, but a response that was
      // already in the microtask queue when the abort landed would not, and it
      // is exactly the stale snapshot this guard exists to keep out.
      if (controller.signal.aborted) return
      setFeed({ status: 'ready', data: res.entries })
    } catch (error) {
      if (isAbort(error) || controller.signal.aborted) return
      setFeed({ status: 'error', message: errorMessage(error) })
    } finally {
      if (loading.current === controller) loading.current = null
    }
  }, [])

  useEffect(() => {
    void load()
    return () => loading.current?.abort()
  }, [load])

  /** Picking a type resets the sentiment to that type's default. */
  function chooseType(next: ComposableType) {
    setType(next)
    setSentiment(DEFAULT_SENTIMENT[next])
  }

  async function addEntry() {
    if (inFlight.current) return

    /* Exactly what the textarea held when the button was pressed, untrimmed.
       The composer is never disabled on network state — that is the rule this
       screen is built on — so the user can and does start their next entry
       while this POST is in flight. Clearing the textarea unconditionally on
       success would then delete text the server never saw and the user never
       submitted. The clear below removes THIS string and only this string. */
    const submitted = text

    const content = submitted.trim()
    if (!content) {
      // Say so rather than returning into silence — an unexplained no-op reads
      // as a broken button. Nothing typed is touched.
      setSaveError('Write something first.')
      return
    }

    inFlight.current = true
    setSaving(true)
    setSaveError(null)
    try {
      await postJournal({
        type,
        content,
        // A free note carries no sentiment even if one is left over in state.
        sentiment: SENTIMENT_SETS[type].length > 0 ? sentiment : null,
      })
      // Functional, and conditional on the textarea still holding what was
      // sent. If the user typed while the POST was in flight, `current` is
      // their new draft and it is left exactly where they left it.
      setText((current) => (current === submitted ? '' : current))
      // Re-read rather than appending locally: the server assigns the id and the
      // date (its today, in MILO_TIMEZONE — not this browser's), and an
      // optimistic row would have to invent both.
      void load()
    } catch (error) {
      // Every character stays where the user left it.
      setSaveError(errorMessage(error))
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  const sentiments = SENTIMENT_SETS[type]
  const entries = feed.status === 'ready' ? feed.data : []

  return (
    <>
      {/* One strip at a time: a failed write first, then the standing "the feed
          did not load" notice. The composer works under both. */}
      {saveError ? (
        <Alert tone="danger" role="alert" title="Not saved.">
          {saveError}
        </Alert>
      ) : feed.status === 'error' ? (
        <Alert tone="danger" role="alert" title="Can’t read your journal.">
          {feed.message} You can still write — nothing you type is lost.
        </Alert>
      ) : null}

      <Window title="NEW-ENTRY.TXT" shadow="m">
        <div className="milo-journal-form">
          <div className="milo-chips" role="group" aria-label="Entry type">
            {TYPE_CHIPS.map((chip) => (
              <button
                key={chip.type}
                type="button"
                className="milo-chip"
                aria-pressed={chip.type === type}
                onClick={() => chooseType(chip.type)}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {sentiments.length > 0 ? (
            <div
              className="milo-chips milo-chips--sentiment"
              role="group"
              aria-label="How was it?"
            >
              {sentiments.map((value) => (
                <button
                  key={value}
                  type="button"
                  className="milo-chip"
                  aria-pressed={value === sentiment}
                  onClick={() => setSentiment(value)}
                >
                  {SENTIMENT_LABEL[value]}
                </button>
              ))}
            </div>
          ) : null}

          <textarea
            className="milo-textarea"
            rows={3}
            placeholder="Say it straight. The coach reads this."
            aria-label="Journal entry"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          <Button
            variant="primary"
            iconRight="＋"
            disabled={saving}
            aria-busy={saving}
            onClick={() => void addEntry()}
          >
            Add entry
          </Button>
        </div>
      </Window>

      {/* The feed. Rendered in the order the server sent it — (date desc, id
          desc) — with no client-side sort or dedupe. */}
      <div className="milo-journal-list">
        {feed.status === 'loading' ? (
          <div className="milo-journal-state">LOADING THE JOURNAL…</div>
        ) : feed.status === 'error' ? (
          <div className="milo-journal-state" role="status">
            THE FEED DID NOT LOAD ✦ THE ENTRIES ARE STILL THERE
          </div>
        ) : entries.length === 0 ? (
          <div className="milo-journal-state">
            NOTHING IN THE LAST {FEED_DAYS} DAYS ✦ WRITE THE FIRST ONE ABOVE —
            HOW A MEAL LANDED, HOW A SESSION FELT, OR ANYTHING THE COACH SHOULD
            KNOW.
          </div>
        ) : (
          entries.map((entry) => {
            const tag = TYPE_TAG[entry.type]
            return (
              <article className="milo-journal-card" key={entry.id}>
                <div className="milo-journal-head">
                  <span className="milo-journal-date">{monoDate(entry.date)}</span>
                  <span className="milo-journal-gap" />
                  <span className={`milo-journal-type milo-journal-type--${tag.modifier}`}>
                    {tag.label}
                  </span>
                  {entry.sentiment ? (
                    <span className="milo-journal-sentiment">
                      {SENTIMENT_LABEL[entry.sentiment]}
                    </span>
                  ) : null}
                </div>
                <div className="milo-journal-body">{entry.content}</div>
              </article>
            )
          })
        )}
      </div>
    </>
  )
}

export default Journal
